<script module lang="ts">
  export interface FindingFilter {
    status: string | null;
    topics: string[] | null;
    kind: SecurityFindingKind | null;
    provider?: string | null;
    disabled?: boolean;
  }
</script>

<script lang="ts">
  import type { SecurityFinding } from "$lib/stores/orgSecurityFindings";
  import { map, uniq } from "lodash";
  import FindingKindSelect from "./FindingKindSelect.svelte";
  import FindingStatusSelect, { type FindingStatus } from "./FindingStatusSelect.svelte";
  import FindingTopicFilter from "./FindingTopicFilter.svelte";
  import { SecurityFindingKind } from "$lib/models/base";
  import FindingProviderSelect from "./FindingProviderSelect.svelte";

  const {
    findings,
    filters,
    onChange,
    disabled = false,
  }: {
    findings: SecurityFinding[];
    filters: FindingFilter;
    onChange: (filters: FindingFilter) => void;
    disabled?: boolean;
  } = $props();

  const topics = $derived.by(() => {
    const topicCounts: { [key: string]: number } = {};
    (findings || []).forEach((finding: SecurityFinding) => {
      finding.sensitiveTopics?.forEach((topic) => {
        const topicName = topic.sensitiveTopic;
        if (topicCounts[topicName]) {
          topicCounts[topicName]++;
        } else {
          topicCounts[topicName] = 1;
        }
      });
    });
    return Object.entries(topicCounts).map(([topic, count]) => ({ topic, count }));
  });
  const findingKindOptions = $derived.by(() => {
    return uniq(map(findings, (finding) => finding.securityFindingKind));
  });
  const hasAtMostOneKind = $derived.by(() => {
    return findingKindOptions.length <= 1;
  });
  const onlyHasOneProvider = $derived.by(() => {
    return uniq(map(findings, (finding) => finding.system)).length === 1;
  });
</script>

<div class="py-2">
  <div class="flex gap-2">
    <div class="flex-1 max-w-[180px]">
      <FindingStatusSelect
        {disabled}
        selectAll
        placeholder={"All Statuses"}
        value={filters.status}
        onChange={(status: FindingStatus | null) => onChange({ ...filters, status })}
      />
    </div>
    {#if !hasAtMostOneKind}
      <div class="flex-1 max-w-[240px]">
        <FindingKindSelect
          {disabled}
          selectAll
          options={findingKindOptions}
          placeholder={"All Kinds"}
          value={filters.kind}
          onChange={(kind: SecurityFindingKind | null) => onChange({ ...filters, kind })}
          sameWidth={false}
        />
      </div>
    {/if}
    {#if !onlyHasOneProvider}
      <div class="flex-1 max-w-[180px]">
        <FindingProviderSelect
          {disabled}
          selectAll
          placeholder={"All Providers"}
          value={filters.provider ?? undefined}
          readonlyStatuses={uniq(map(findings, (finding) => finding.system))}
          onChange={(provider: string | null) => onChange({ ...filters, provider })}
        />
      </div>
    {/if}
  </div>
  <FindingTopicFilter
    {disabled}
    {topics}
    onChange={(selectedTopics?: string[]) => {
      onChange({ ...filters, topics: selectedTopics ?? null });
    }}
  />
</div>
